import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  UpdateBundleContentPayload,
  UpdateBundleOfferPayload,
  UpdateBundlePricingPayload,
  UpdateBundleRulesPayload,
  getBundle,
  listBundles,
  updateBundleContent,
  updateBundleOffer,
  updateBundlePricing,
  updateBundleRules,
} from '~/services/AccommodationService';

export const bundleQueryKeys = {
  all: ['bundles'] as const,
  lists: () => [...bundleQueryKeys.all, 'list'] as const,
  list: (filters: { limit?: number; page?: number }) => [...bundleQueryKeys.lists(), filters] as const,
  details: () => [...bundleQueryKeys.all, 'detail'] as const,
  detail: (bundleId: string) => [...bundleQueryKeys.details(), bundleId] as const,
};

interface UseGetBundlesParams {
  limit?: number;
  page?: number;
  enabled?: boolean;
}

export function useGetBundles({ limit, page, enabled = true }: UseGetBundlesParams = {}) {
  return useQuery({
    queryKey: bundleQueryKeys.list({ limit, page }),
    queryFn: async ({ signal }) => {
      const response = await listBundles({ limit, page, signal });
      return response.result;
    },
    enabled,
  });
}

export function useGetAllBundles({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: bundleQueryKeys.list({ limit: 1000, page: 1 }),
    queryFn: async ({ signal }) => {
      const response = await listBundles({ limit: 1000, page: 1, signal });
      return response.result;
    },
    enabled,
  });
}

interface UseGetBundleParams {
  bundleId: string;
  enabled?: boolean;
}

export function useGetBundle({ bundleId, enabled = true }: UseGetBundleParams) {
  return useQuery({
    queryKey: bundleQueryKeys.detail(bundleId),
    queryFn: async ({ signal }) => {
      const response = await getBundle({ bundleId, signal });
      return response.result;
    },
    enabled: enabled && !!bundleId,
  });
}

export function useUpdateBundlePricing() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ bundleId, payload }: { bundleId: string; payload: UpdateBundlePricingPayload }) => {
      return updateBundlePricing({ bundleId, payload });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
      enqueueSnackbar('Bundle pricing updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update pricing: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateBundleRules() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ bundleId, payload }: { bundleId: string; payload: UpdateBundleRulesPayload }) => {
      return updateBundleRules({ bundleId, payload });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
      enqueueSnackbar('Bundle rules updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update rules: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateBundleContent() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ bundleId, payload }: { bundleId: string; payload: UpdateBundleContentPayload }) => {
      return updateBundleContent({ bundleId, payload });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
      enqueueSnackbar('Bundle content updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update content: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useUpdateBundleOffer() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      bundleId,
      offerId,
      payload,
    }: {
      bundleId: string;
      offerId: string;
      payload: UpdateBundleOfferPayload;
    }) => {
      return updateBundleOffer({ bundleId, offerId, payload });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.detail(variables.bundleId) });
      queryClient.invalidateQueries({ queryKey: bundleQueryKeys.lists() });
      enqueueSnackbar('Bundle offer updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update offer: ${error.message}`, { variant: 'error' });
    },
  });
}
